import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { recordingSocket } from "../mediasoup/recordingSocket";
import PublicCauseList from "./PublicCauseList";
import PublicViewerPlayer from "./PublicViewerPlayer";
import PublicStreamHeader from "./PublicStreamHeader";
import logger from "../../utils/logger";

type StreamStatus =
  | "waiting"
  | "scheduled"
  | "live"
  | "paused"
  | "rec"
  | "ended";

const extractCnrFromHlsUrl = (
  hlsUrl: string | undefined,
): string | undefined => {
  if (!hlsUrl) return undefined;
  const match = hlsUrl.match(/\/hls\/([^/]+)\/index\.m3u8/);
  if (!match) return undefined;
  const parts = match[1].split("_");
  if (parts.length < 3) return undefined;
  return parts.slice(1, -1).join("_");
};
const STATUS_FRESHNESS_MS = 8000;

// fixed video : cause-list ratio — applied as flexBasis so it holds on TV,
// laptop, and mobile alike (row on lg+, column below lg)
const VIDEO_BASIS = "70%";
const LIST_BASIS = "30%";

export default function PublicViewerPage() {
  const { courtRoomNo } = useParams();
  const [roomInfo, setRoomInfo] = useState<any>(null);

  const [status, setStatus] = useState<StreamStatus>("waiting");
  const [playerStreamPath, setPlayerStreamPath] = useState<string | null>(null);
  const [showResumedBanner, setShowResumedBanner] = useState(false);
  const lastValidStreamPath = useRef<string | null>(null);
  const prevStatusRef = useRef<StreamStatus>("waiting");
  const resumeBannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [neighborCauses, setNeighborCauses] = useState<{
    prev: { cnr?: string; petitioner?: string; respondent?: string } | null;
    next: { cnr?: string; petitioner?: string; respondent?: string } | null;
  }>({ prev: null, next: null });

  const currentSessionCnr = useRef<string | undefined>(undefined);
  const [liveCnr, setLiveCnr] = useState<string | undefined>(undefined);
  const sessionStatusMap = useRef<
    Map<string, { status: "live" | "paused" | "idle"; ts: number }>
  >(new Map());

  const [activeCause, setActiveCause] = useState<{
    cnr?: string;
    petitioner?: string;
    respondent?: string;
  } | null>(null);

  const [cnrLiveStatus, setCnrLiveStatus] = useState<
    "live" | "paused" | "idle"
  >("idle");

  useEffect(() => {
    if (!recordingSocket) return;

    const handleStreamStatus = (data: any) => {
      if (!data?.sessionId) return;

      if (data.status === "live" || data.status === "paused") {
        sessionStatusMap.current.set(data.sessionId, {
          status: data.status,
          ts: Date.now(),
        });
      } else if (data.status === "draining" || data.status === "available") {
        sessionStatusMap.current.set(data.sessionId, {
          status: "idle",
          ts: Date.now(),
        });
      }

      if (data.sessionId === currentSessionCnr.current) {
        const entry = sessionStatusMap.current.get(data.sessionId);
        setCnrLiveStatus(entry?.status || "idle");
      }
    };

    const handleStop = ({ sessionId }: any) => {
      sessionStatusMap.current.set(sessionId, {
        status: "idle",
        ts: Date.now(),
      });
      if (sessionId === currentSessionCnr.current) setCnrLiveStatus("idle");
    };

    recordingSocket.on("stream-status", handleStreamStatus);
    recordingSocket.on("stream_stop", handleStop);
    return () => {
      recordingSocket.off("stream-status", handleStreamStatus);
      recordingSocket.off("stream_stop", handleStop);
    };
  }, []);

  const handleCourtroomUpdate = useCallback((data: any) => {
    logger.log("RAW courtroom_stream_update PAYLOAD:", JSON.stringify(data));
    const incomingCnr = data.sessionId || extractCnrFromHlsUrl(data.hlsUrl);
    if (incomingCnr && incomingCnr !== currentSessionCnr.current) {
      currentSessionCnr.current = incomingCnr;
      setLiveCnr(incomingCnr);
      const entry = sessionStatusMap.current.get(incomingCnr);
      const isFresh = entry && Date.now() - entry.ts < STATUS_FRESHNESS_MS;
      setCnrLiveStatus(isFresh ? entry!.status : "idle");
    }

    if (data.status === "switching") return;

    const prevStatus = prevStatusRef.current;

    const effectiveStatus = data.mode === "rec-only" ? "rec" : data.status;
    logger.log(
      `Effective status: ${effectiveStatus}, Previous status: ${prevStatus}`,
    );

    if (prevStatus === "paused" && effectiveStatus === "live") {
      setShowResumedBanner(true);
      if (resumeBannerTimer.current) clearTimeout(resumeBannerTimer.current);
      resumeBannerTimer.current = setTimeout(() => {
        setShowResumedBanner(false);
      }, 4000);
    }

    if (effectiveStatus === "live" && data.hlsUrl) {
      const fullPath = !data.hlsUrl.startsWith("http")
        ? `${import.meta.env.VITE_APP_STREAM_SERVER}${data.hlsUrl}`
        : data.hlsUrl;
      lastValidStreamPath.current = fullPath;
      setPlayerStreamPath(fullPath);
      setStatus("live");
      prevStatusRef.current = "live";
      return;
    }

    if (effectiveStatus === "live" && !data.hlsUrl) {
      if (prevStatus === "paused" && lastValidStreamPath.current) {
        setPlayerStreamPath(lastValidStreamPath.current);
      } else {
        setPlayerStreamPath(null);
      }
      setStatus("live");
      prevStatusRef.current = "live";
      return;
    }

    if (effectiveStatus === "paused") {
      setStatus("paused");
      prevStatusRef.current = "paused";
      return;
    }

    if (effectiveStatus === "rec") {
      setStatus("rec");
      setPlayerStreamPath(null);
      prevStatusRef.current = "rec";
      return;
    }

    setStatus(effectiveStatus);
    prevStatusRef.current = effectiveStatus;

    if (effectiveStatus === "ended" || effectiveStatus === "waiting") {
      setPlayerStreamPath(null);
      lastValidStreamPath.current = null;
      if (currentSessionCnr.current) {
        sessionStatusMap.current.set(currentSessionCnr.current, {
          status: "idle",
          ts: Date.now(),
        });
      }
      currentSessionCnr.current = undefined;
      setLiveCnr(undefined);
      setCnrLiveStatus("idle");
    }
  }, []);

  useEffect(() => {
    if (!courtRoomNo) return;
    recordingSocket.emit("watch_courtroom", {
      courtRoomNo,
      roomId: courtRoomNo,
    });
    recordingSocket.on("courtroom_stream_update", handleCourtroomUpdate);
    return () => {
      recordingSocket.off("courtroom_stream_update", handleCourtroomUpdate);
      if (resumeBannerTimer.current) clearTimeout(resumeBannerTimer.current);
    };
  }, [courtRoomNo, handleCourtroomUpdate]);

  const displayStatus: StreamStatus =
    status === "rec"
      ? "rec"
      : status === "waiting" || status === "scheduled" || status === "ended"
        ? status
        : status === "paused"
          ? "paused"
          : cnrLiveStatus === "paused"
            ? "paused"
            : status === "live" || cnrLiveStatus === "live"
              ? "live"
              : "waiting";

  const isLive = displayStatus === "live" && !!playerStreamPath;
  const isPaused = displayStatus === "paused";
  const isRecording = displayStatus === "rec";
  const courtRoomLabel = roomInfo?.benchName;

  console.log("roomInfo", roomInfo);

  return (
    <div className="flex flex-col w-full h-screen bg-gray-100 overflow-hidden">
      <PublicStreamHeader
        roomInfo={roomInfo}
        activeCause={activeCause}
        prevCause={neighborCauses.prev}
        nextCause={neighborCauses.next}
        courtRoomLabel={courtRoomLabel}
        displayStatus={displayStatus}
      />

      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
        <div
          className="w-full border-b lg:border-b-0 lg:border-r border-gray-200 overflow-hidden"
          style={{ flexBasis: LIST_BASIS, flexGrow: 0, flexShrink: 0 }}
        >
          <PublicCauseList
            courtRoomId={courtRoomNo || ""}
            onActiveCauseChange={setActiveCause}
            onNeighborsChange={setNeighborCauses}
            onRoomInfoChange={setRoomInfo}
            streamStatus={displayStatus}
            liveCnr={liveCnr}
          />
        </div>

        <div
          className="w-full min-w-0 bg-black"
          style={{ flexBasis: VIDEO_BASIS, flexGrow: 1, flexShrink: 1 }}
        >
          <PublicViewerPlayer
            isLive={isLive}
            isPaused={isPaused}
            isRecording={isRecording}
            showResumedBanner={showResumedBanner}
            streamPath={playerStreamPath || ""}
            user="Public Viewer"
            caseId={courtRoomNo || "unknown"}
            courtRoomLabel={courtRoomLabel}
            courtName={courtRoomLabel || "Unknown Court"}
            ip=""
            cnr={activeCause?.cnr}
          />
        </div>
      </div>
    </div>
  );
}
